import React, { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import MUIDataTable from "mui-datatables";
import Loader from "../../../components/loader/Loader";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { addonSchema } from "../../../app/schema";
import {
  handlePageSettings,
  hideConfirmModal,
} from "../../../redux/counterSlice";
import { formatDate, parseStatusColor } from "../../../app/utlils";
import ThreeDotMenu from "./ThreeDotMenu";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LaptopIcon from "@mui/icons-material/Laptop";
import { StarRateRounded } from "@mui/icons-material";
import { usePermission } from "../../../hooks/usePermission";
import { Payment } from "@mui/icons-material";

function OrderList({ type }) {
  const dispatch = useDispatch();

  const loginType = sessionStorage.getItem("loginType") || "admin";
  const loggedInShopId = Number(sessionStorage.getItem("shop_id")) || 0;

  const getPageSettingsKey = () => {
    switch (type) {
      case "cancelled":
        return "cancelledOrder";
      case "failed":
        return "failedOrder";
      case "delivered":
        return "deliveredOrder";
      default:
        return "ongoingOrder";
    }
  };

  const pageSettingsKey = getPageSettingsKey();

  const pageSettings = useSelector(
    (state) =>
      state.counter.pageSettings?.[pageSettingsKey] || {
        pageSize: 10,
        pageNo: 0,
      },
  );

  const { pageSize, pageNo } = pageSettings;

  const getMenuName = () => {
    switch (type) {
      case "cancelled":
        return "Cancelled Orders";
      case "failed":
        return "Failed Orders";
      case "delivered":
        return "Delivered Orders";
      default:
        return "Ongoing Orders";
    }
  };

  const permissions = usePermission(getMenuName());

  const methods = useForm({ resolver: yupResolver(addonSchema) });
  const { reset } = methods;

  const [loader, setLoader] = useState(true);
  const [formData, setFormData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedRow, setSelectedRow] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusCounts, setStatusCounts] = useState({
    Submitted: 0,
    Confirmed: 0,
    Dispatched: 0,
    ReadyToDelivered: 0,
    Delivered: 0,
  });

  // Check if user has permission to view (skip for shop login)
  useEffect(() => {
    if (loginType !== "shop" && !permissions.view) {
      toast.error("You don't have permission to view this page");
    }
  }, [permissions.view, loginType]);

  const loadInitialData = () => {
    let endpoint = "/listAllOrders";
    if (type === "cancelled") endpoint = "/listCancelledOrders";
    else if (type === "failed") endpoint = "/listAllFailedOrders";
    else if (type === "delivered") endpoint = "/listAllDeliveredOrdersPaginate";

    const page = pageNo + 1;
    const url = `${endpoint}?page=${page}&per_page=${pageSize}`;

    setLoader(true);

    axios
      .get(url)
      .then((response) => {
        const data = response?.data?.data || {};
        let orders = data?.data || [];

        if (loginType === "shop" && loggedInShopId > 0) {
          orders = orders.filter(
            (order) =>
              order?.shop_id === loggedInShopId ||
              order?.shops?.shop_details_id === loggedInShopId ||
              order?.shop_details_id === loggedInShopId,
          );
        }

        setFormData(orders);
        setTotalCount(loginType === "shop" ? orders.length : data?.total || 0);

        const counts = orders.reduce((acc, item) => {
          const status = item.order_status;
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        }, {});
        setStatusCounts((prev) => ({ ...prev, ...counts }));
      })
      .catch((err) => toast.error(err?.message))
      .finally(() => {
        setLoader(false);
        dispatch(hideConfirmModal({ msg: "" }));
      });
  };

  useEffect(() => {
    if (loginType === "shop" || permissions.view) {
      reset();
      loadInitialData();
    }
  }, [pageNo, pageSize, type, permissions.view, loginType, loggedInShopId]);

  const hasActionPermission =
    loginType === "shop" ? true : permissions?.edit || permissions?.delete;

  const columns = [
    {
      name: "order_id",
      label: "ID",
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex) => {
          return formData[dataIndex]?.meta_data
            ? `${formData[dataIndex]?.order_id}(${formData[dataIndex]?.meta_data})`
            : formData[dataIndex]?.order_id;
        },
      },
    },
    { name: "delivery_mode", label: "Mode" },
    {
      name: "region.city.name",
      label: "Location",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const district =
            formData[dataIndex]?.ordered_address?.address_city_district;
          const shopName = formData[dataIndex]?.shops?.shop_name;
          const mode = formData[dataIndex]?.delivery_mode;
          return <p>{mode === "Door Step Delivery" ? district : shopName}</p>;
        },
        filter: true,
        filterOptions: {
          names: [
            ...new Set(formData?.map((item) => item?.region?.city?.name)),
          ],
          logic(location, filters) {
            if (filters.length) return !filters.includes(location);
            return false;
          },
        },
      },
    },
    { name: "customer_details.customer_name", label: "Name" },
    { name: "customer_details.customer_mobile", label: "Mobile" },
    {
      name: "order_submitted_at",
      label: "Placed At",
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex) => {
          const order_submitted_at = formData[dataIndex]?.order_submitted_at;
          return formatDate(new Date(order_submitted_at));
        },
      },
    },
    {
      name: "expected_delivery",
      label: "Expected",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const expected_delivery = formData[dataIndex]?.expected_delivery;
          const slot_timing = formData[dataIndex]?.slot_timing;
          const date = new Date(expected_delivery);
          const formattedDate = `${date.getDate().toString().padStart(2, "0")}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date.getFullYear()}`;
          return (
            <>
              <p>{formattedDate}</p>
              <p>{slot_timing}</p>
            </>
          );
        },
      },
    },
    {
      name: "order_overall_totall",
      label: "Amount",
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex) => {
          const amount = formData[dataIndex]?.order_overall_totall;
          return `₹${amount}`;
        },
      },
    },
    {
      name: "attempt",
      label: "Attempt",
      options: {
        display: type === "failed",
        customBodyRenderLite: (dataIndex) => {
          const attempt = formData[dataIndex]?.attempt;
          return <Chip label={attempt} size="small" color="primary" />;
        },
      },
    },
    {
      name: "payment_details",
      label: "Payment",
      options: {
        filter: false,
        download: false,
        customBodyRenderLite: (dataIndex) => {
          const razorpayId = formData[dataIndex]?.razorpay_payment_id || "N/A";
          const invoiceNo = formData[dataIndex]?.invoice_no || "N/A";
          const tooltipContent = (
            <div style={{ padding: "4px" }}>
              <div style={{ marginBottom: "4px" }}>
                <strong>Razorpay ID:</strong> {razorpayId}
              </div>
              <div style={{ marginBottom: "4px" }}>
                <strong>Invoice No:</strong> {invoiceNo}
              </div>
            </div>
          );
          return (
            <Tooltip title={tooltipContent} arrow placement="top">
              <Payment
                color="primary"
                fontSize="small"
                style={{ cursor: "pointer" }}
              />
            </Tooltip>
          );
        },
      },
    },
    {
      name: "product_quantity",
      label: "Platform",
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex) => {
          const platform = formData[dataIndex]?.platform;
          if (platform == null || platform === "" || platform === "ANDROID")
            return <PhoneAndroidIcon color="primary" fontSize="small" />;
          if (platform === "WEB")
            return <LaptopIcon color="primary" fontSize="small" />;
          return `${platform}`;
        },
      },
    },
    {
      name: "ratings",
      label: "Ratings",
      options: {
        filter: false,
        download: false,
        display: type === "delivered",
        customBodyRenderLite: (dataIndex) => {
          const rating = +formData[dataIndex]?.customer_ratings || 0;
          const comment = formData[dataIndex]?.comment_customer_review || "";
          if (rating === 0) return <span>N/A</span>;
          return (
            <Tooltip
              title={comment || ""}
              arrow
              disableHoverListener={!comment}
            >
              <span>
                {[1, 2, 3, 4, 5].map((star) => (
                  <StarRateRounded
                    key={star}
                    fontSize="small"
                    sx={{
                      color: star <= rating ? "#FFC107" : "#E0E0E0",
                      cursor: comment ? "pointer" : "default",
                    }}
                  />
                ))}
              </span>
            </Tooltip>
          );
        },
      },
    },
    {
      name: "order_status",
      label: "Status",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const status = formData[dataIndex]?.order_status;
          const { bgColor, textColor } = parseStatusColor(status);
          return (
            <Chip
              label={status}
              size="small"
              sx={{ background: bgColor, color: textColor }}
            />
          );
        },
      },
    },
    {
      name: "message",
      label: "Reason",
      options: {
        // display: type === "failed",
        display: false,
        customBodyRenderLite: (dataIndex) => {
          const attempt = formData[dataIndex]?.message;
          return <Chip label={attempt} size="small" color="primary" />;
        },
      },
    },
    ...(hasActionPermission
      ? [
          {
            name: "Edit",
            options: {
              filter: false,
              download: false,
              customBodyRenderLite: (dataIndex) => {
                const dataString = formData[dataIndex]?.all_failed_order_ids;
                return (
                  <ThreeDotMenu
                    id={formData[dataIndex]?.order_id}
                    type={type}
                    additionalData={dataString}
                    permissions={
                      loginType === "shop"
                        ? { view: true, edit: true, delete: true }
                        : permissions
                    }
                    onConfirmAction={(rowData) => {
                      setSelectedRow({
                        ...rowData,
                        confirmLabel: "delete this order",
                        action: () => {
                          setConfirmOpen(false);
                          loadInitialData();
                        },
                      });
                      setConfirmOpen(true);
                    }}
                  />
                );
              },
            },
          },
        ]
      : []),
  ];

  const options = {
    enableNestedDataAccess: ".",
    serverSide: true,
    count: totalCount,
    page: pageNo,
    rowsPerPage: pageSize,
    selectableRows: false,
    download: false,
    viewColumns: true,
    print: false,
    filter: true,
    searchText,
    download: true,
    onSearchChange: (searchValue) => setSearchText(searchValue),
    onChangePage: (newPage) => {
      dispatch(
        handlePageSettings({
          screen: pageSettingsKey,
          pageSize,
          pageNo: newPage,
        }),
      );
    },
    onChangeRowsPerPage: (numberOfRows) => {
      dispatch(
        handlePageSettings({
          screen: pageSettingsKey,
          pageSize: numberOfRows,
          pageNo: 0,
        }),
      );
    },
  };

  const StatusButtons = () => {
    if (["cancelled", "failed", "delivered"].includes(type)) return null;
    const statusList = [
      "Submitted",
      "Confirmed",
      "Dispatched",
      "ReadyToDelivered",
      "Delivered",
    ];
    return (
      <div className="status-buttons-container">
        <h6 className="mb-0">Status Count:</h6>
        {statusList.map((item) => {
          const { bgColor, textColor } = parseStatusColor(item);
          return (
            <Badge
              key={item}
              showZero
              badgeContent={statusCounts[item]}
              sx={{
                "& .MuiBadge-badge": {
                  color: textColor,
                  backgroundColor: bgColor,
                },
              }}
            />
          );
        })}
      </div>
    );
  };

  if (loginType !== "shop" && !permissions.view) {
    return (
      <Stack sx={{ width: "100%" }} spacing={2}>
        <Alert severity="error">
          <h6>You don't have permission to view this page.</h6>
        </Alert>
      </Stack>
    );
  }

  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          <h4 className="text-dark">Order List</h4>
          {formData.length > 0 ? (
            <MUIDataTable
              data={formData}
              columns={columns}
              options={options}
              title={<StatusButtons />}
            />
          ) : (
            <Stack sx={{ width: "100%" }} spacing={2}>
              <Alert severity="warning">
                <h6>No Data Found..</h6>
              </Alert>
            </Stack>
          )}
        </>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {selectedRow?.confirmLabel} ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmOpen(false)}
            variant="contained"
            color="error"
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={() => selectedRow?.action()}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default OrderList;
